export const TRANSLATION_ERROR_TYPES = {
    disabled        : "disabled",
    missingKey      : "missing_key",
    authError       : "auth_error",
    modelError      : "model_error",
    rateLimited     : "rate_limited",
    timeout         : "timeout",
    network         : "network",
    serverError     : "server_error",
    invalidResponse : "invalid_response",
    unknown         : "unknown"
};

const TRANSLATION_ERROR_DETAILS = {
    [TRANSLATION_ERROR_TYPES.disabled]: {
        label  : "AI translation is disabled",
        reason : "Dynamic text translation is disabled by configuration."
    },
    [TRANSLATION_ERROR_TYPES.missingKey]: {
        label  : "Translation API key is missing",
        reason : "AI translation is not configured for this environment."
    },
    [TRANSLATION_ERROR_TYPES.authError]: {
        label  : "Translation API configuration error",
        reason : "Check API key permissions or backend translation configuration."
    },
    [TRANSLATION_ERROR_TYPES.modelError]: {
        label  : "Translation model is not available",
        reason : "The selected AI translation model cannot be used."
    },
    [TRANSLATION_ERROR_TYPES.rateLimited]: {
        label  : "Translation limit reached",
        reason : "AI translation is temporarily unavailable. Try again after the quota resets."
    },
    [TRANSLATION_ERROR_TYPES.timeout]: {
        label  : "Translation service is not responding",
        reason : "The translation request timed out. Dynamic text translation was disabled to avoid extra API requests."
    },
    [TRANSLATION_ERROR_TYPES.network]: {
        label  : "Translation service is not responding",
        reason : "The translation service could not be reached. Dynamic text translation was disabled to avoid extra API requests."
    },
    [TRANSLATION_ERROR_TYPES.serverError]: {
        label  : "Translation service is not responding",
        reason : "The translation service returned an error. Dynamic text translation was disabled to avoid extra API requests."
    },
    [TRANSLATION_ERROR_TYPES.invalidResponse]: {
        label  : "Translation service returned an invalid response",
        reason : "The translation result could not be used safely. Dynamic text translation was disabled."
    },
    [TRANSLATION_ERROR_TYPES.unknown]: {
        label  : "Translation failed",
        reason : "Dynamic text translation was disabled to avoid extra API requests."
    }
};

const getErrorDetails = (type) => TRANSLATION_ERROR_DETAILS[type] ||
    TRANSLATION_ERROR_DETAILS[TRANSLATION_ERROR_TYPES.unknown];

export const getHttpErrorType = (status) => {
    if (status === 401 || status === 403) return TRANSLATION_ERROR_TYPES.authError;
    if (status === 404) return TRANSLATION_ERROR_TYPES.modelError;
    if (status === 429) return TRANSLATION_ERROR_TYPES.rateLimited;

    return TRANSLATION_ERROR_TYPES.serverError;
};

export const isRetryableErrorType = (type, status) => {
    if (type === TRANSLATION_ERROR_TYPES.timeout) return true;
    if (type === TRANSLATION_ERROR_TYPES.network) return true;
    if (type === TRANSLATION_ERROR_TYPES.invalidResponse) return true;

    return type === TRANSLATION_ERROR_TYPES.serverError && (!status || status >= 500);
};

export const createTranslationError = (type, message, options = {}) => {
    const details = getErrorDetails(type);
    const error = new Error(message || details.reason || details.label);

    error.type = type || TRANSLATION_ERROR_TYPES.unknown;
    error.status = options.status || null;
    error.retryable = options.retryable !== undefined ?
        !!options.retryable :
        isRetryableErrorType(error.type, error.status);
    error.disableApi = options.disableApi !== undefined ? !!options.disableApi : true;
    error.label = options.label || details.label;
    error.reason = options.reason || details.reason;

    return error;
};

export const normalizeTranslationError = (error, fallbackMessage = "Translation failed") => {
    if (error && error.type) {
        const details = getErrorDetails(error.type);

        error.status = error.status || null;
        error.retryable = error.retryable !== undefined ?
            !!error.retryable :
            isRetryableErrorType(error.type, error.status);
        error.disableApi = error.disableApi !== undefined ? !!error.disableApi : true;
        error.label = error.label || details.label;
        error.reason = error.reason || details.reason;

        return error;
    }

    if (error && error.name === "AbortError") {
        return createTranslationError(
            TRANSLATION_ERROR_TYPES.timeout,
            error.message || fallbackMessage,
            { retryable: true }
        );
    }

    if (error && error.name === "TypeError") {
        return createTranslationError(
            TRANSLATION_ERROR_TYPES.network,
            error.message || fallbackMessage,
            { retryable: true }
        );
    }

    return createTranslationError(
        TRANSLATION_ERROR_TYPES.unknown,
        error && error.message ? error.message : fallbackMessage,
        { retryable: false }
    );
};

export const isRetryableTranslationError = (error) => {
    const normalizedError = normalizeTranslationError(error);

    return !!normalizedError.retryable;
};

export const shouldDisableTranslationApi = (error) => {
    const normalizedError = normalizeTranslationError(error);

    return normalizedError.disableApi !== false;
};

export const isRateLimitedTranslationError = (error) => {
    return error && error.type === TRANSLATION_ERROR_TYPES.rateLimited;
};

export const validateTranslationResult = (result, items, serviceName = "Translation") => {
    if (!result || !Array.isArray(result.items)) {
        throw createTranslationError(
            TRANSLATION_ERROR_TYPES.invalidResponse,
            `${ serviceName } response has invalid items`,
            { retryable: true }
        );
    }

    const expectedKeys = items.map((item) => item.key);
    const expectedKeyMap = {};
    const translatedKeyMap = {};

    expectedKeys.forEach((key) => {
        expectedKeyMap[key] = true;
    });

    for (let item of result.items) {
        if (!item || typeof item.key !== "string" || typeof item.translatedText !== "string") {
            throw createTranslationError(
                TRANSLATION_ERROR_TYPES.invalidResponse,
                `${ serviceName } response item is invalid`,
                { retryable: true }
            );
        }

        if (!expectedKeyMap[item.key] || translatedKeyMap[item.key]) {
            throw createTranslationError(
                TRANSLATION_ERROR_TYPES.invalidResponse,
                `${ serviceName } response keys do not match request`,
                { retryable: true }
            );
        }

        translatedKeyMap[item.key] = true;
    }

    if (Object.keys(translatedKeyMap).length !== expectedKeys.length) {
        throw createTranslationError(
            TRANSLATION_ERROR_TYPES.invalidResponse,
            `${ serviceName } response is incomplete`,
            { retryable: true }
        );
    }

    return result;
};
